"use client";

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const ShortcutsHelpDialog = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleShowShortcuts = () => {
      setOpen(true);
    };

    window.addEventListener('show-shortcuts-help', handleShowShortcuts);
    return () => window.removeEventListener('show-shortcuts-help', handleShowShortcuts);
  }, []);

  const shortcuts = [
    {
      category: 'Global',
      items: [
        { keys: ['⌘', 'K'], description: 'Open command palette' },
        { keys: ['⌘', '/'], description: 'Show keyboard shortcuts' },
        { keys: ['⌘', 'N'], description: 'New item (context-aware)' },
      ]
    },
    {
      category: 'Navigation',
      items: [
        { keys: ['⌘', '1'], description: 'Go to Dashboard' },
        { keys: ['⌘', '2'], description: 'Go to Clients' },
        { keys: ['⌘', '3'], description: 'Go to Projects' },
        { keys: ['⌘', '4'], description: 'Go to Invoices' },
      ]
    },
    {
      category: 'Quick Actions',
      items: [
        { keys: ['⌘', 'Shift', 'C'], description: 'New Client' },
        { keys: ['⌘', 'Shift', 'P'], description: 'New Project' },
        { keys: ['⌘', 'Shift', 'I'], description: 'New Invoice' },
        { keys: ['⌘', 'Shift', 'T'], description: 'New Task' },
      ]
    }
  ];

  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const formatKey = (key: string) => {
    if (!isMac && key === '⌘') return 'Ctrl';
    return key;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogDescription>
            Speed up your workflow with these keyboard shortcuts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {shortcuts.map((category) => (
            <div key={category.category}>
              <h3 className="font-medium text-sm text-muted-foreground mb-3">
                {category.category}
              </h3>
              
              <div className="space-y-2">
                {category.items.map((shortcut, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, keyIndex) => (
                        <Badge key={keyIndex} variant="outline" className="px-2 py-1 text-xs">
                          {formatKey(key)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {category.category !== shortcuts[shortcuts.length - 1].category && (
                <Separator className="mt-4" />
              )}
            </div>
          ))}
        </div>

        <div className="text-xs text-muted-foreground mt-6">
          {isMac ? 'Use ⌘ (Cmd) key' : 'Use Ctrl key'} for shortcuts
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShortcutsHelpDialog;